import React, { useState } from 'react';
import { useP } from '../i18n/useP';
import './LiquidityChart.css';

/**
 * LIQUIDITY CHART — le solde de trésorerie en fin de mois, sur douze mois
 * glissants (proto Olivier du 2026-10-16). C'est l'indicateur qui donne le
 * profil et la tendance du cash : un point par mois, la ligne du zéro, et le
 * seuil minimal cible en pointillé, la zone qui le sépare du zéro teintée.
 *
 * Le seuil se règle sur le graphique : un clic sur son étiquette ouvre une
 * saisie, qui écrit la même hypothèse que le plan de liquidité (`cash_buffer`).
 * Il n'existe qu'UNE valeur de seuil.
 *
 * Props :
 *   serie     : douze soldes (null pour un mois sans donnée)
 *   labels    : douze libellés de mois
 *   threshold : seuil en francs (ou null)
 *   fmt       : (francs) => texte
 *   onSave    : (francs) => Promise, absent = seuil en lecture seule
 *   projectedFrom : rang du premier mois PROJETÉ (vue de l'exercice) : la ligne
 *               passe en pointillé et les points se creusent à partir de là
 */
export default function LiquidityChart({ serie, labels, threshold, fmt, onSave, projectedFrom = null }) {
  const p = useP();
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState('');
  const vals = (serie || []).filter((v) => Number.isFinite(v));
  if (!serie || serie.length < 2 || vals.length < 2) return null;

  const W = 560; const H = 150; const PL = 40; const PR = 8; const PT = 14; const PB = 22;
  const seuil = Number.isFinite(threshold) && threshold > 0 ? threshold : null;
  const min = Math.min(0, ...vals);
  const max = Math.max(seuil || 0, ...vals) * 1.05 || 1;
  const span = max - min || 1;
  const x = (i) => PL + ((W - PL - PR) * i) / (serie.length - 1);
  const y = (v) => PT + (H - PT - PB) * (1 - (v - min) / span);
  const pts = serie.map((v, i) => (Number.isFinite(v) ? { i, v, x: x(i), y: y(v) } : null)).filter(Boolean);
  const last = pts[pts.length - 1];
  const below = seuil != null && last.v < seuil;

  const open = () => { setDraft(String(Math.round(seuil || 0))); setEdit(true); };
  const apply = () => {
    const n = parseInt(String(draft).replace(/[^0-9]/g, ''), 10);
    setEdit(false);
    if (Number.isFinite(n) && onSave) onSave(n);
  };

  return (
    <div className="lk-lchart">
      <svg className="lk-lchart__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={p('Solde de trésorerie en fin de mois, douze mois')}>
        {seuil != null && (
          <rect className="lk-lchart__zone" x={PL} y={y(seuil)} width={W - PL - PR} height={Math.max(0, y(0) - y(seuil))} />
        )}
        <line className="lk-lchart__zero" x1={PL} x2={W - PR} y1={y(0)} y2={y(0)} />
        {seuil != null && <line className="lk-lchart__seuil" x1={PL} x2={W - PR} y1={y(seuil)} y2={y(seuil)} />}
        {projectedFrom == null ? (
          <polyline className="lk-lchart__line" fill="none" points={pts.map((q) => `${q.x},${q.y}`).join(' ')} />
        ) : (
          <>
            <polyline className="lk-lchart__line" fill="none" points={pts.filter((q) => q.i < projectedFrom).map((q) => `${q.x},${q.y}`).join(' ')} />
            <polyline className="lk-lchart__line lk-lchart__line--proj" fill="none"
                      points={pts.filter((q) => q.i >= projectedFrom - 1).map((q) => `${q.x},${q.y}`).join(' ')} />
            {projectedFrom > 0 && projectedFrom < serie.length && (
              <text className="lk-lchart__lbl" x={x(projectedFrom) - 4} y={PT - 3} textAnchor="end">{p('réel')}</text>
            )}
            {projectedFrom > 0 && projectedFrom < serie.length && (
              <text className="lk-lchart__lbl" x={x(projectedFrom) + 4} y={PT - 3}>{p('prévision')}</text>
            )}
          </>
        )}
        {pts.map((q) => (
          <circle key={q.i} className={`lk-lchart__dot${q === last ? ' is-last' : ''}${q === last && below ? ' is-low' : ''}${projectedFrom != null && q.i >= projectedFrom ? ' is-proj' : ''}`}
                  cx={q.x} cy={q.y} r={q === last ? 4 : 2.6} />
        ))}
        {(labels || []).map((lb, i) => (projectedFrom != null || i % 2 === 0 || i === labels.length - 1) && (
          <text key={i} className="lk-lchart__lbl" x={x(i)} y={H - 6} textAnchor="middle">{lb}</text>
        ))}
        <text className="lk-lchart__lbl" x={PL - 4} y={y(max / 1.05) + 3} textAnchor="end">{fmt(max / 1.05)}</text>
        <text className="lk-lchart__lbl" x={PL - 4} y={y(0) + 3} textAnchor="end">0</text>
      </svg>
      {seuil != null && (
        <button type="button" className="lk-lchart__seuil-btn" style={{ top: `${(y(seuil) / H) * 100}%` }}
                onClick={onSave ? open : undefined} disabled={!onSave}>
          {p('Seuil minimal')} {fmt(seuil)}{onSave ? ' ✎' : ''}
        </button>
      )}
      {seuil == null && onSave && (
        <button type="button" className="lk-lchart__seuil-add" onClick={open}>{p('Fixer un seuil minimal')}</button>
      )}
      {edit && (
        <div className="lk-lchart__pop">
          <input autoFocus inputMode="numeric" value={draft} aria-label={p('Seuil minimal')}
                 onChange={(e) => setDraft(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') apply(); if (e.key === 'Escape') setEdit(false); }} />
          <button type="button" className="lk-lchart__ok" onClick={apply}>{p('Appliquer')}</button>
          <button type="button" onClick={() => setEdit(false)}>{p('Annuler')}</button>
          <span>{p('Modifie le seuil minimal cible du plan de liquidité, en francs.')}</span>
        </div>
      )}
    </div>
  );
}
